import React, { forwardRef, useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import { CalendarDays } from "lucide-react";

import { useBreadcrumbs } from "../../../context/breadcrumb/breadcrumb-context.tsx";
import PageShell from "../../shared/page-shell/page-shell.tsx";
import { LeaveTypeLabel } from "../../shared/leave-type-label/leave-type-label.tsx";
import { compareValues } from "../../ui/table/data-table.tsx";
import type { SortDirection } from "../../ui/table/types.ts";
import {
  ResponsiveDataTable,
  type ResponsiveColumn,
} from "../../ui/responsive-table/index.ts";
import { DatePicker } from "../../ui/date-picker/date-picker.tsx";
import { useTodayLeaves } from "../../../hooks/use-today-leaves.ts";
import styles from "./team-availability-page.module.css";
import { formatToLongDate } from "../../../utils/helpers.ts";
import { TailSpin } from "react-loader-spinner";
import { EmptyStateMessage } from "../../shared/empty-state-message/empty-state-message.tsx";

export type LeaveFilterValue = "all" | "casual" | "sick" | "wfh";

export type TeamAvailabilityStatus = "active" | "pending";
export type TeamAvailabilityRow = {
  id: string;
  name: string;
  role: string;
  leaveCount: number;
  reason: string;
  leaveType: string;
  leaveKey: Exclude<LeaveFilterValue, "all">;
  from: Date;
  to: Date;
  fromLabel: string;
  toLabel: string;
  status: TeamAvailabilityStatus;
} & Record<string, unknown>;

function startOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function rangesOverlap(
  aStart: Date,
  aEnd: Date,
  bStart: Date,
  bEnd: Date,
): boolean {
  const as = startOfDay(aStart).getTime();
  const ae = startOfDay(aEnd).getTime();
  const bs = startOfDay(bStart).getTime();
  const be = startOfDay(bEnd).getTime();
  return as <= be && ae >= bs;
}

type TeamAvailabilityDateTriggerProps = React.ComponentPropsWithoutRef<"button"> & {
  selectedDate: Date | null;
  placeholderText?: string;
};

const TeamAvailabilityDateTrigger = forwardRef<
  HTMLButtonElement,
  TeamAvailabilityDateTriggerProps
>(function TeamAvailabilityDateTrigger(
  { selectedDate, placeholderText = "Select date", className, ...rest },
  ref
) {
  const text =
    selectedDate != null ? selectedDate.toLocaleDateString() : "";
  const hasValue = text !== "";

  return (
    <button
      ref={ref}
      type="button"
      className={clsx(styles.dateFilterTrigger, className)}
      {...rest}
    >
      <CalendarDays
        size={18}
        strokeWidth={2}
        className={styles.dateFilterTriggerIcon}
        aria-hidden
      />
      <span
        className={clsx(
          styles.dateFilterTriggerLabel,
          !hasValue && styles.dateFilterTriggerPlaceholder
        )}
      >
        {hasValue ? text : placeholderText}
      </span>
    </button>
  );
});

TeamAvailabilityDateTrigger.displayName = "TeamAvailabilityDateTrigger";

const TABLE_COLUMNS: ResponsiveColumn<TeamAvailabilityRow>[] = [
  {
    id: "employee",
    header: "Employee Name",
    accessor: "name",
    sortable: true,
    render: (row) => (
      <span className={styles.employeeCell}>
        {/* <Avatar name={row.name} size="md" /> */}
        <span className={styles.employeeName}>{row.name}</span>
      </span>
    ),
  },
  // { id: "role", header: "Role", accessor: "role", sortable: true },
  {
    id: "from",
    header: "From",
    accessor: "fromLabel",
    getSortValue: (row) => row.from,
    sortable: true,
  },
  {
    id: "to",
    header: "To",
    accessor: "toLabel",
    getSortValue: (row) => row.to,
    sortable: true,
  },
  {
    id: "leaveType",
    header: "Leave type",
    accessor: "leaveType",
    sortable: true,
    render: (row) => (
      <LeaveTypeLabel
        variant={row.leaveKey === "wfh" ? "wfh" : "leave"}
        label={row.leaveType}
      />
    ),
  },
  {
    id: "leaveCount",
    header: "Leave Count",
    accessor: "leaveCount",
    sortable: true,
  },
  // {
  //   id: "reason",
  //   header: "Reason",
  //   accessor: "reason",
  //   sortable: false,
  //   truncate: true,
  // },
];

function sortTeamRows(
  rows: TeamAvailabilityRow[],
  sortKey: string,
  sortDir: Exclude<SortDirection, null>,
): TeamAvailabilityRow[] {
  const col = TABLE_COLUMNS.find((c) => c.id === sortKey);
  if (!col) return rows;
  const getVal = (row: TeamAvailabilityRow) =>
    col.getSortValue != null
      ? col.getSortValue(row)
      : col.accessor != null
        ? row[col.accessor]
        : null;
  if (col.getSortValue == null && col.accessor == null) {
    return rows;
  }
  const copy = [...rows];
  copy.sort((a, b) => {
    const cmp = compareValues(getVal(a), getVal(b));
    return sortDir === "asc" ? cmp : -cmp;
  });
  return copy;
}

export default function TeamAvailabilityPage() {
  const { setBreadcrumbs } = useBreadcrumbs();
  const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());
  const { data, loading } = useTodayLeaves(selectedDate ?? undefined);

  const mappedRows: TeamAvailabilityRow[] = useMemo(
    () =>
      data.map((item) => ({
        id: String(item.laveId),
        name: item.name,
        role: item.email,
        leaveType: item.typeOfLeave,
        leaveKey: "casual",
        from: new Date(item.startDate),
        to: new Date(item.endDate),
        fromLabel: formatToLongDate(item.startDate),
        toLabel: formatToLongDate(item.endDate),
        status: item.leaveFinalStatus === "PENDING" ? "pending" : "active",
        leaveCount: item.noOfDays,
        reason: item.reasonOfLeave,
      })),
    [data],
  );
  const [leaveFilter, setLeaveFilter] = useState<LeaveFilterValue>("all");
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [sortKey, setSortKey] = useState("from");
  const [sortDir, setSortDir] = useState<Exclude<SortDirection, null>>("asc");

  useEffect(() => {
    setBreadcrumbs([
      { label: "Home", path: "/dashboard" },
      { label: "Team Availability" },
    ]);
    return () => setBreadcrumbs([]);
  }, [setBreadcrumbs]);

  const filtered = useMemo(() => {
    return mappedRows.filter((row) => {
      if (leaveFilter !== "all" && row.leaveKey !== leaveFilter) {
        return false;
      }
      if (startDate != null && endDate != null) {
        if (!rangesOverlap(row.from, row.to, startDate, endDate)) {
          return false;
        }
      }
      return true;
    });
  }, [mappedRows, leaveFilter, startDate, endDate]);

  const sortedFiltered = useMemo(
    () => sortTeamRows(filtered, sortKey, sortDir),
    [filtered, sortKey, sortDir],
  );

  const memberStatLabel = sortedFiltered.length === 1 ? "Member" : "Members";

  const handleSort = (key: string | undefined, dir: SortDirection) => {
    if (key == null || dir == null) {
      setSortKey("from");
      setSortDir("asc");
      return;
    }
    setSortKey(key);
    setSortDir(dir);
  };

  return (
    <PageShell>
      {loading && (
        <div className="overlay">
          <div className="loader-container">
            <TailSpin
              height="100"
              width="100"
              radius="1"
              color="blue"
              ariaLabel="tail-spin-loading"
              visible={true}
            />
          </div>
        </div>
      )}
      <h1 className="visually-hidden">Team Availability</h1>
      <div className={styles.toolbar}>
        {/* <div className={styles.filterSlot}>
          <Select
            label="Leave type"
            value={leaveFilter}
            onChange={(v) => setLeaveFilter(v as LeaveFilterValue)}
            options={[...LEAVE_TYPE_OPTIONS]}
          />
        </div> */}
        <div className={styles.filterSlot}>
          <div className={styles.dateFilter}>
            <label
              className={clsx("sub-heading-sm", styles.dateFilterLabel)}
              htmlFor="team-availability-date"
            >
              Select Date
            </label>
            <DatePicker
              selected={selectedDate}
              onChange={(d) => setSelectedDate(d)}
              customInput={
                <TeamAvailabilityDateTrigger
                  id="team-availability-date"
                  selectedDate={selectedDate}
                  placeholderText="Select date"
                />
              }
            />
          </div>
        </div>
      </div>
      <div className={styles.tableSection}>
        <p
          className={clsx("sub-heading-sm", styles.totalStat)}
          aria-live="polite"
        >
          {sortedFiltered.length} {memberStatLabel}
        </p>
        <ResponsiveDataTable<TeamAvailabilityRow>
          pagination={false}
          columns={TABLE_COLUMNS}
          data={sortedFiltered}
          sortKey={sortKey}
          sortDir={sortDir}
          onSortChange={handleSort}
          aria-label="Team availability"
          emptyState={
            !loading ? <EmptyStateMessage /> : undefined
          }
        />
      </div>
    </PageShell>
  );
}
